import { track, trackLogin } from '@services/tracking';
import { rgb } from '@design-system/tokens';
import { createElement, type CSSProperties } from 'react';
import toast from 'react-hot-toast';
import type { SessionUser } from '../model/auth.types';
import { getRegisterContinueEvent } from '../model/registration.model';
import type { RegisterStep } from './register.types';

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
export const INVALID_CODE_ERROR_MESSAGE =
  'Invalid confirmation code. Please try again.';
export const EXISTING_USER_ERROR_MESSAGE =
  'An account with this email already exists. Please sign in instead.';
const REGISTRATION_TRACKED_VALUES_SESSION_KEY =
  'auth-2.registration.tracked-values';
const EMPTY_TRACKED_VALUE = '__EMPTY__';
const INVALID_CODE_TOAST_ID = 'auth-invalid-code-toast';
const EXISTING_USER_TOAST_ID = 'auth-existing-user-toast';
const INVALID_CODE_TOAST_MESSAGE = 'Invalid code';
const EXISTING_USER_TOAST_MESSAGE = 'Account already exists. Please sign in.';
const EXISTING_USER_TOAST_ACTION_LABEL = 'Sign in';
const INVALID_CODE_TOAST_ACTION_LABEL = 'Resend';
const AUTH_ERROR_SNACKBAR_CONTAINER_STYLE: CSSProperties = {
  minWidth: 'auto',
  maxWidth: '520px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  background: rgb('W-100'),
  borderRadius: '4px',
  boxShadow:
    '0px 6px 10px 0px rgba(100, 100, 100, 0.15), 0px 2px 3px 0px rgba(100, 100, 100, 0.3)',
  padding: '4px 8px 4px 16px',
};
const AUTH_ERROR_SNACKBAR_LEADING_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  minWidth: 0,
};
const AUTH_ERROR_SNACKBAR_ICON_STYLE: CSSProperties = {
  color: rgb('ERROR-100'),
  fontFamily: '"Visuelt Pro", "Visuelt", sans-serif',
  fontSize: '24px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  flexShrink: 0,
};
const AUTH_ERROR_SNACKBAR_TEXT_STYLE: CSSProperties = {
  color: rgb('N-100'),
  fontFamily: '"Visuelt Pro", "Visuelt", sans-serif',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  width: '250px',
};
const AUTH_ERROR_SNACKBAR_ACTION_STYLE: CSSProperties = {
  border: 0,
  background: 'transparent',
  margin: 0,
  padding: '10px 12px',
  borderRadius: '100px',
  color: rgb('P-80'),
  cursor: 'pointer',
  fontFamily: '"Visuelt Pro", "Visuelt", sans-serif',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
};

type NotifyInvalidCodeErrorOptions = {
  onResend?: () => void;
};

type RegistrationTrackedValues = Record<string, Array<string>>;

const toTrackedValueForSession = (value: string | null): string => {
  const trackedValue = (value ?? '').trim();
  return trackedValue.length > 0 ? trackedValue : EMPTY_TRACKED_VALUE;
};

const readTrackedValuesFromSession = (): RegistrationTrackedValues => {
  const rawTrackedValues = sessionStorage.getItem(
    REGISTRATION_TRACKED_VALUES_SESSION_KEY,
  );
  const parsedTrackedValues = rawTrackedValues
    ? (JSON.parse(rawTrackedValues) as unknown)
    : {};

  if (
    !parsedTrackedValues ||
    typeof parsedTrackedValues !== 'object' ||
    Array.isArray(parsedTrackedValues)
  ) {
    return {};
  }

  return parsedTrackedValues as RegistrationTrackedValues;
};

const shouldTrackEventValueInSession = (
  eventName: string,
  trackedValue: string,
): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const trackedValuesByEvent = readTrackedValuesFromSession();
    const trackedValuesForEvent = Array.isArray(trackedValuesByEvent[eventName])
      ? trackedValuesByEvent[eventName]
      : [];
    const nextTrackedValues = new Set(
      trackedValuesForEvent.filter(
        (value): value is string => typeof value === 'string',
      ),
    );

    if (nextTrackedValues.has(trackedValue)) {
      return false;
    }

    nextTrackedValues.add(trackedValue);
    sessionStorage.setItem(
      REGISTRATION_TRACKED_VALUES_SESSION_KEY,
      JSON.stringify({
        ...trackedValuesByEvent,
        [eventName]: Array.from(nextTrackedValues),
      }),
    );
    return true;
  } catch {
    return true;
  }
};

export const trackContinueAction = (
  step: RegisterStep,
  trackedValue: string | null,
): void => {
  const eventName = getRegisterContinueEvent(step);
  if (!eventName) {
    return;
  }

  const sessionTrackedValue = toTrackedValueForSession(trackedValue);

  if (!shouldTrackEventValueInSession(eventName, sessionTrackedValue)) {
    return;
  }

  track({ type: eventName, payload: { uid: '' } });
};

export const trackMobilePhoneSubmitted = (phoneValue: string | null): void => {
  const sessionPhoneValue = toTrackedValueForSession(phoneValue);
  if (
    !shouldTrackEventValueInSession('Mobile Phone Submitted', sessionPhoneValue)
  ) {
    return;
  }

  track({
    type: 'Mobile Phone Submitted',
    payload: {
      uid: '',
      'Left Empty': sessionPhoneValue === EMPTY_TRACKED_VALUE,
    },
  });
};

export const trackSuccessfulLogin = (user: SessionUser): void => {
  trackLogin({
    uid: `${user.id}`,
    'First Name': user.firstName,
    'Last Name': user.lastName,
    Email: user.email,
    'Playbook Creator ID': user.id,
  });
};

export const trackSignInWithSso = (
  user: SessionUser,
  provider: string,
): void => {
  track({
    type: 'Sign In With SSO',
    payload: { uid: `${user.id}`, Provider: provider },
  });
};

export const notifyInvalidCodeError = (
  options: NotifyInvalidCodeErrorOptions = {},
): void => {
  toast.custom(
    (toastItem) =>
      createElement(
        'div',
        {
          style: AUTH_ERROR_SNACKBAR_CONTAINER_STYLE,
        },
        createElement(
          'div',
          {
            style: AUTH_ERROR_SNACKBAR_LEADING_STYLE,
          },
          createElement(
            'span',
            {
              style: AUTH_ERROR_SNACKBAR_ICON_STYLE,
              'aria-hidden': true,
            },
            '✖',
          ),
          createElement(
            'span',
            {
              style: AUTH_ERROR_SNACKBAR_TEXT_STYLE,
            },
            INVALID_CODE_TOAST_MESSAGE,
          ),
        ),
        createElement(
          'button',
          {
            type: 'button',
            style: AUTH_ERROR_SNACKBAR_ACTION_STYLE,
            onClick: () => {
              options.onResend?.();
              toast.dismiss(toastItem.id);
            },
          },
          INVALID_CODE_TOAST_ACTION_LABEL,
        ),
      ),
    {
      id: INVALID_CODE_TOAST_ID,
      duration: 5000,
      position: 'top-right',
    },
  );
};

type NotifyExistingUserErrorOptions = {
  onSignIn?: () => void;
};

export const notifyExistingUserError = (
  options: NotifyExistingUserErrorOptions = {},
): void => {
  toast.custom(
    (toastItem) =>
      createElement(
        'div',
        {
          style: AUTH_ERROR_SNACKBAR_CONTAINER_STYLE,
        },
        createElement(
          'div',
          {
            style: AUTH_ERROR_SNACKBAR_LEADING_STYLE,
          },
          createElement(
            'span',
            {
              style: AUTH_ERROR_SNACKBAR_ICON_STYLE,
              'aria-hidden': true,
            },
            '✖',
          ),
          createElement(
            'span',
            {
              style: AUTH_ERROR_SNACKBAR_TEXT_STYLE,
            },
            EXISTING_USER_TOAST_MESSAGE,
          ),
        ),
        createElement(
          'button',
          {
            type: 'button',
            style: AUTH_ERROR_SNACKBAR_ACTION_STYLE,
            onClick: () => {
              options.onSignIn?.();
              toast.dismiss(toastItem.id);
            },
          },
          EXISTING_USER_TOAST_ACTION_LABEL,
        ),
      ),
    {
      id: EXISTING_USER_TOAST_ID,
      duration: 5000,
      position: 'top-right',
    },
  );
};
